import React, { Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ButtonWithLoadingState } from "../../common/components/button-with-loading-state/ButtonWithLoadingState";
import CommonDialog from "../../common/components/dialog/CommonDialog";
import styles from "./BrandingLoadingDialog.module.scss";

const SLOW_LOADING_DELAY_MS = 3000;

export interface BrandingLoadingDialogProps {
	isError: boolean;
	onRetry: () => void;
	onContinue: () => void;
}

// The texts suspend until the customer namespace loads, so each sits in its own <Suspense>
// (this dialog renders above the app's only boundary in index.tsx); the dots need no
// translation and show right away.
const JustAMoment: React.FC = () => {
	const { t } = useTranslation();
	return <div className={styles.description}>{t("branding.justAMoment")}</div>;
};

const ErrorContent: React.FC<Omit<BrandingLoadingDialogProps, "isError">> = ({ onRetry, onContinue }) => {
	const { t } = useTranslation();
	return (
		<>
			<div className={styles.title}>{t("branding.weCouldntConnectRightNow")}</div>
			<div className={styles.description}>{t("branding.checkYourInternetConnection")}</div>
			<div className={styles.buttons}>
				{/* Branding (and the tenant's colors) isn't loaded yet, so the button color is fixed. */}
				<ButtonWithLoadingState
					buttonText={t("branding.tryAgain")}
					buttonPressed={onRetry}
					style={{ backgroundColor: "var(--app-royal-blue)" }}
				/>
				<ButtonWithLoadingState buttonText={t("branding.continueAnyway")} buttonPressed={onContinue} isSecondary />
			</div>
		</>
	);
};

/** Covers the app while the utility's branding loads; on error the user can retry or continue with the default configuration. */
const BrandingLoadingDialog: React.FC<BrandingLoadingDialogProps> = ({ isError, onRetry, onContinue }) => {
	const [isLoadingSlow, setIsLoadingSlow] = useState(false);

	// Restarts on every new load, including a retry after an error.
	useEffect(() => {
		if (isError) return;
		setIsLoadingSlow(false);
		const timer = setTimeout(() => setIsLoadingSlow(true), SLOW_LOADING_DELAY_MS);
		return () => clearTimeout(timer);
	}, [isError]);

	return (
		<CommonDialog onCloseDialog={isError ? onContinue : undefined} borderRadius="30px">
			<div className={styles.container}>
				{isError ? (
					<Suspense fallback={null}>
						<ErrorContent onRetry={onRetry} onContinue={onContinue} />
					</Suspense>
				) : (
					<>
						<div className={styles.dots} aria-hidden="true">
							<span />
							<span />
							<span />
						</div>
						{isLoadingSlow && (
							<Suspense fallback={null}>
								<JustAMoment />
							</Suspense>
						)}
					</>
				)}
			</div>
		</CommonDialog>
	);
};

export default BrandingLoadingDialog;
